import { Content } from '.generated/Feature.CW.model';
import classNames from 'classnames';
import { withStandardComponentWrapper } from 'helpers/HOC';
import ComponentWrapper from 'helpers/SitecoreWrappers/ComponentWrapper/ComponentWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import useIsEditing from 'lib/hooks/useIsEditing';
import React from 'react';

export type TextLinkListProps = Content.TextLinkList_Component & {
  fields?: {
    column1?: Content.LinkGroup_Component[];
    column2?: Content.LinkGroup_Component[];
    column3?: Content.LinkGroup_Component[];
  };
  isSidenavPart?: boolean;
};

const columns = ['column1', 'column2', 'column3'] as const;

const getTotalColumn = (fields: TextLinkListProps['fields'] = {}) =>
  columns.reduce((count, col) => (fields[col]?.length ? count + 1 : count), 0);

const renderColumn = (
  items: Content.LinkGroup_Component[] | undefined,
  isEditing: boolean,
  colIndex: number
) =>
  (items?.length || isEditing) && (
    <div className="col-span-1" key={colIndex}>
      {items?.map((item, index) => (
        <div className="mb-12 last:mb-0" key={`linkgroup-${colIndex}-${index}`}>
          {(item?.fields?.title?.value !== '' || isEditing) && (
            <h4
              className={classNames(
                'font-centuryGothic text-22 leading-31 font-bold mb-8 text-theme-subtext'
              )}
            >
              <PlainTextWrapper field={item?.fields?.title} />
            </h4>
          )}

          <RichTextWrapper tag="div" field={item?.fields?.textArea} />
        </div>
      ))}
    </div>
  );

const TextLinkList = (props: TextLinkListProps) => {
  const isEditing = useIsEditing();
  const totalColumn = getTotalColumn(props?.fields);

  const gridClasses = classNames(
    'grid gap-12 md:grid-cols-2',
    totalColumn >= 2 && 'md:grid-cols-2',
    totalColumn === 3 && 'lg:grid-cols-3'
  );

  const wrapperClasses = classNames(
    props?.params?.FieldNames?.toLowerCase() === 'cardvariant'
      ? 'shadow-default bg-white p-12 rounded-[8px]'
      : ''
  );

  if (!isEditing && totalColumn === 0) {
    return <></>;
  }

  return (
    <ComponentWrapper
      params={props?.params}
      isSidenavPart={props?.isSidenavPart}
      disableThemeOutsideSideNav={!props?.isSidenavPart}
      disableThemeInsideSideNav={props?.params?.FieldNames?.toLowerCase() == 'cardvariant'}
    >
      <div className="max-w-screen-xxl w-full px-10 lg:px-20 mx-auto">
        <div className={wrapperClasses}>
          {(props?.fields?.heading?.value !== '' || isEditing) && (
            <h3
              className={classNames(
                'font-bold text-[#021134] font-centuryGothic relative before:absolute before:left-0 before:bottom-0 before:h-[1px] before:w-full pb-4 mb-12 text-24 leading-28 lg:text-26 lg:leading-31',
                props?.params?.FieldNames?.toLowerCase() === 'flatvariant'
                  ? !props?.isSidenavPart
                    ? 'before:bg-[#D9D9D9]'
                    : 'before:bg-theme-row-spliter-border-color'
                  : 'before:bg-[#D9D9D9]'
              )}
            >
              <PlainTextWrapper field={props?.fields?.heading} />
            </h3>
          )}

          <div className={gridClasses}>
            {columns.map((col, idx) => renderColumn(props?.fields?.[col], isEditing, idx))}
          </div>
        </div>
      </div>
    </ComponentWrapper>
  );
};

export const BlogResourcesWrapper = (props: TextLinkListProps): JSX.Element => {
  return <TextLinkList {...props} />;
};

export const Default = withStandardComponentWrapper(TextLinkList);
export const CardVariant = withStandardComponentWrapper(TextLinkList);
export const FlatVariant = withStandardComponentWrapper(TextLinkList);
